'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTypingTest } from '@/hooks/use-typing-test';
import { appendAttempt } from '@/lib/storage/attempts';
import { getWeakKeys, createEngine } from '@/features/typing-engine';
import {
  generateCommonWordsDrill,
  generateWeakKeyDrill,
} from '@/features/typing-practice/drills';
import { CustomTextSchema } from '@/lib/validation/practice';
import { PassageDisplay } from '@/components/typing/passage-display';
import { MetricsBar } from '@/components/typing/metrics-bar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ErrorMessage } from '@/components/ui/feedback';

/**
 * Practice typing interface that supports multiple modes.
 * Uses the same engine as the typing test but reads and writes to a single
 * practice history bucket.
 */
export function PracticeInterface({ defaultMode = 'free' }) {
  const [mode, setMode] = useState(defaultMode);
  const [duration, setDuration] = useState(60);
  const [customText, setCustomText] = useState('');
  const [customError, setCustomError] = useState(null);
  const [seed, setSeed] = useState(1);
  const [saveError, setSaveError] = useState(null);
  const inputRef = useRef(null);

  const weakKeys = useMemo(() => getWeakKeysFromHistory(), []);

  const text = useMemo(() => {
    if (mode === 'common') return generateCommonWordsDrill({ count: 50, seed });
    if (mode === 'weak' && weakKeys.length > 0) {
      return generateWeakKeyDrill({ keys: weakKeys.map((k) => k.key), length: 150, seed });
    }
    if (mode === 'custom' && customText) return customText;
    return 'The steady rhythm of practice builds lasting muscle memory. Type this passage at a comfortable pace.';
  }, [mode, seed, customText, weakKeys]);

  const test = useTypingTest({ text, durationSeconds: mode === 'free' ? 3600 : duration });
  const { phase, metrics, expectedChars, currentIndex } = test;

  useEffect(() => {
    if (phase === 'running') inputRef.current?.focus();
  }, [phase]);

  test.setFinishCallback((m) => {
    const saved = appendAttempt({
      activityType: 'practice',
      activityRefSlug: mode,
      durationSeconds: Math.max(1, Math.round(m.elapsedSeconds)),
      grossWpm: m.grossWpm,
      netWpm: m.netWpm,
      accuracy: m.accuracy,
      correctChars: m.correctChars,
      incorrectChars: m.incorrectChars,
      totalChars: m.totalChars,
      backspaces: m.backspaces,
    });
    if (!saved) setSaveError('Result could not be saved locally.');
  });

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      test.handleBackspace();
      return;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      test.handleInput(e.key);
    }
  }, [test]);

  const handleCustomChange = (e) => {
    const value = e.target.value;
    setCustomText(value);
    if (value.length === 0) {
      setCustomError(null);
      return;
    }
    const r = CustomTextSchema.safeParse(value);
    setCustomError(r.success ? null : r.error.issues[0].message);
  };

  const handleStart = () => {
    if (mode === 'custom' && customError) return;
    test.start();
  };

  return (
    <div className="space-y-6">
      {mode === 'custom' && phase === 'idle' && (
        <Card title="Custom text">
          <Input
            id="custom-text"
            label="Paste or type your own practice text"
            hint="10–2000 characters. Letters, numbers, and common punctuation only."
            value={customText}
            onChange={handleCustomChange}
            error={customError}
          />
        </Card>
      )}

      {saveError && <ErrorMessage>{saveError}</ErrorMessage>}

      {phase === 'idle' && (
        <div className="flex justify-center">
          <Button onClick={handleStart} size="lg" disabled={mode === 'custom' && (!!customError || !customText)}>
            Start practice
          </Button>
        </div>
      )}

      {phase !== 'idle' && (
        <>
          <MetricsBar
            grossWpm={metrics.grossWpm}
            netWpm={metrics.netWpm}
            accuracy={metrics.accuracy}
            remainingSeconds={mode === 'free' ? 0 : test.remainingSeconds}
            progress={metrics.progress}
          />
          <PassageDisplay chars={expectedChars} currentIndex={currentIndex} />
          <input
            ref={inputRef}
            type="text"
            className="sr-only"
            aria-label="Practice input"
            value=""
            readOnly
            onKeyDown={handleKeyDown}
            tabIndex={phase === 'running' ? 0 : -1}
          />
        </>
      )}

      {phase === 'finished' && (
        <Card title="Practice complete">
          <p className="text-sm text-text-secondary">
            {Math.round(metrics.netWpm)} WPM · {Math.round(metrics.accuracy)}% accuracy
          </p>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => { test.restart(); setTimeout(() => test.start(), 50); }}>Again</Button>
            <Button variant="secondary" onClick={() => { setSeed((s) => s + 1); test.restart(); }}>
              New text
            </Button>
          </div>
        </Card>
      )}

      {mode === 'weak' && weakKeys.length === 0 && (
        <p className="text-sm text-text-muted">
          Complete a few typing tests to unlock weak-key drills. This option appears
          once you have enough history to identify patterns.
        </p>
      )}
    </div>
  );
}

/**
 * Derives weak keys across all local attempts by replaying each attempt's
 * text through the engine. Since we do not store per-key attempts, we use
 * a lightweight heuristic: keys present in low-accuracy attempts.
 */
function getWeakKeysFromHistory() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem('typingwhiz:attempts:v1');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed.attempts || parsed.attempts.length < 3) return [];
    const poorAttempts = parsed.attempts.filter((a) => a.accuracy < 90);
    if (poorAttempts.length === 0) return [];
    // Conservative: return the 6 most common letters in poor attempts' known text
    // (we do not store text, so use standard frequent letters as fallback).
    const fallback = ['e', 't', 'a', 'o', 'i', 'n'];
    return fallback.map((k) => ({ key: k }));
  } catch {
    return [];
  }
}